/**
 * Withdrawal Currency Display Manager
 * 
 * Manages currency display throughout the withdrawal modal with proper formatting.
 * 
 * @package betbeast
 */

import { CurrencyFormatter } from '../deposit-flow/currency-formatter.js';

export class WithdrawalCurrencyDisplayManager {
    constructor(modalElement) {
        this.modal = modalElement;
        this.formatter = new CurrencyFormatter();
        this.currentCurrency = 'EUR';
        
        if (!this.modal) {
            console.warn('Modal element not provided to WithdrawalCurrencyDisplayManager');
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize currency display
     */
    init() {
        // Get currency from modal data attributes
        const currencyElement = this.modal.querySelector('.wallet-withdrawal-currency');
        if (currencyElement) {
            this.currentCurrency = currencyElement.textContent.trim();
        }
        
        // Format all currency displays
        this.updateMinMaxDisplay();
        this.updateWithdrawalMethodLimits();
        this.setupInputFormatting();
    }
    
    /**
     * Update withdrawal method limit displays with proper formatting
     */
    updateWithdrawalMethodLimits() {
        const withdrawalMethods = this.modal.querySelectorAll('.wallet-withdrawal-payment-method-item');
        
        withdrawalMethods.forEach(method => {
            const minWithdrawal = parseFloat(method.dataset.minWithdrawal);
            const maxWithdrawal = parseFloat(method.dataset.maxWithdrawal);
            const limitsElement = method.querySelector('.wallet-withdrawal-payment-method-limits');
            
            // Each bound is optional: a method can be configured with only a minimum.
            // With neither, the server-rendered subtitle is left as-is.
            if (limitsElement && (!isNaN(minWithdrawal) || !isNaN(maxWithdrawal))) {
                // Values are already in the player's currency; this only formats them.
                const parts = [];
                if (!isNaN(minWithdrawal)) {
                    parts.push(`Min. ${this.formatter.format(minWithdrawal, this.currentCurrency)}`);
                }
                if (!isNaN(maxWithdrawal)) {
                    parts.push(`Max. ${this.formatter.format(maxWithdrawal, this.currentCurrency)}`);
                }

                limitsElement.textContent = parts.join(' – ');
            }
        });
    }
    
    /**
     * Update min/max display with proper formatting
     */
    updateMinMaxDisplay() {
        const minElement = this.modal.querySelector('.wallet-withdrawal-min');
        const maxElement = this.modal.querySelector('.wallet-withdrawal-max');
        const amountInput = this.modal.querySelector('.wallet-withdrawal-amount-input');
        
        if (!amountInput) return;
        
        // An absent bound means no limit applies — mirror that by clearing the
        // attribute and the label rather than inventing a number that contradicts
        // what is actually enforced.
        const minAmount = parseFloat(amountInput.getAttribute('Minimum'));
        const maxAmount = parseFloat(amountInput.getAttribute('Maximum'));

        // Mirror into the real attributes the validator reads.
        this.applyBound(amountInput, 'min', minAmount);
        this.applyBound(amountInput, 'max', maxAmount);

        if (minElement) {
            minElement.textContent = isNaN(minAmount)
                ? ''
                : `Minimum: ${this.formatter.format(minAmount, this.currentCurrency)}`;
        }

        if (maxElement) {
            maxElement.textContent = isNaN(maxAmount)
                ? ''
                : `Maximum: ${this.formatter.format(maxAmount, this.currentCurrency)}`;
        }

        // Also update the inline display if exists
        const minValueSpan = this.modal.querySelector('.wallet-withdrawal-min-value');
        const maxValueSpan = this.modal.querySelector('.wallet-withdrawal-max-value');

        if (minValueSpan) {
            minValueSpan.textContent = isNaN(minAmount)
                ? ''
                : this.formatter.format(minAmount, this.currentCurrency);
        }

        if (maxValueSpan) {
            maxValueSpan.textContent = isNaN(maxAmount)
                ? ''
                : this.formatter.format(maxAmount, this.currentCurrency);
        }
    }

    /**
     * Set or clear one bound attribute on the amount input.
     *
     * @param {HTMLInputElement} input
     * @param {string} attr - 'min' or 'max'
     * @param {number} value - NaN clears the attribute
     */
    applyBound(input, attr, value) {
        if (isNaN(value)) {
            input.removeAttribute(attr);
        } else {
            input.setAttribute(attr, value);
        }
    }
    
    /**
     * Setup input formatting (format on blur, allow typing numbers)
     */
    setupInputFormatting() {
        const amountInput = this.modal.querySelector('.wallet-withdrawal-amount-input');
        if (!amountInput) return;
        
        // Format on blur
        amountInput.addEventListener('blur', (e) => {
            const value = parseFloat(e.target.value);
            if (value && !isNaN(value)) {
                // Display formatted but keep numeric value in the input for submission
                e.target.dataset.numericValue = value;
            }
        });
        
        // Remove formatting on focus for easy editing
        amountInput.addEventListener('focus', (e) => {
            if (e.target.dataset.numericValue) {
                e.target.value = e.target.dataset.numericValue;
            }
        });
    }
    
    /**
     * Get numeric value from input
     */
    getNumericValue() {
        const amountInput = this.modal.querySelector('.wallet-withdrawal-amount-input');
        if (!amountInput) return 0;
        
        return parseFloat(amountInput.value) || parseFloat(amountInput.dataset.numericValue) || 0;
    }
}
